// /src/axiosInterceptor.js

// English comments only
import axios from "axios";
import { refreshAuthToken } from "../utils/refreshAuthToken";
import { isResumeSafe } from "./wsRecovery/wsRecoveryManager";
import { setBlockLoginAutoRedirect } from "./authSessionStorage";

let authLogoutInFlight = false;

const AUTH_SILENT_URLS = [
  "/api/user/check-token",
  "/api/auth/clear-legacy-cookies",
  "/api/auth/clear-auth-cookies",
];

function isSilentAuthRequest(url) {
  if (!url || typeof url !== "string") return false;
  return AUTH_SILENT_URLS.some((path) => url.includes(path));
}

axios.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    if (!error.response) {
      console.warn("[Axios] ⚠ Network unreachable or backend down:", error.message);

      if (!navigator.onLine || error.message.includes("Network Error")) {
        console.log("[Axios] Offline detected → waiting before retry");
        await waitForNetwork();
        return axios(originalRequest);
      }

      if (error.message.includes("EHOSTUNREACH") || error.message.includes("ECONNREFUSED")) {
        console.log("[Axios] Backend temporarily unreachable → retry later");
        await sleep(5000);
        return axios(originalRequest);
      }

      return Promise.reject(error);
    }

    const status = error.response.status;
    const requestUrl = originalRequest?.url || "";

    if (isSilentAuthRequest(requestUrl)) {
      return Promise.reject(error);
    }

    const isRefreshCall = requestUrl.includes("/api/token/refresh");

    if (isRefreshCall) {
      if (status === 401) {
        console.warn("[Axios] Refresh token invalid → full logout");
        handleLogout(true);
      }
      return Promise.reject(error);
    }

    if (status === 419 && !originalRequest._retry) {
      console.log("[Axios] Token expired (419) → attempting refresh via helper");
      originalRequest._retry = true;

      try {
        const ok = await refreshAuthToken();
        if (ok) {
          console.info("[Axios] Token refreshed → retrying original request");
          return axios(originalRequest);
        }
      } catch (refreshError) {
        console.error("[Axios] Error refreshing token via helper:", refreshError.message);

        if (!navigator.onLine || refreshError.message.includes("EHOSTUNREACH")) {
          console.warn("[Axios] Network unreachable during refresh → waiting");
          await waitForNetwork();
          return axios(originalRequest);
        }

        handleLogout(true);
      }
    }

    if (status === 401) {
      if (isResumeSafe()) {
        console.warn("[Axios] 🛑 401 ignored due to resumeSafe window");
        return Promise.reject(error);
      }

      if (!originalRequest._retry401) {
        originalRequest._retry401 = true;
        try {
          const ok = await refreshAuthToken();
          if (ok) {
            console.info("[Axios] 401 → token refreshed → retrying original request");
            return axios(originalRequest);
          }
        } catch (e) {
          // fall through to logout
        }
      }

      console.warn("[Axios] 401 after refresh attempt → logout");
      handleLogout(true);
    }

    if (status === 503) {
      const retryCount = originalRequest._retry503Count || 0;
      const maxRetries = 2;
      const skipRetry = originalRequest.skip503Retry === true;
      const isAppError =
        error.response?.data?.error != null ||
        error.response?.data?.ok === false;

      if (skipRetry || isAppError || retryCount >= maxRetries) {
        return Promise.reject(error);
      }

      originalRequest._retry503Count = retryCount + 1;
      console.warn(
        `[Axios] 503 Service Unavailable → retry ${originalRequest._retry503Count}/${maxRetries} after delay`
      );
      await sleep(5000);
      return axios(originalRequest);
    }

    return Promise.reject(error);
  }
);

function waitForNetwork() {
  return new Promise(resolve => {
    if (navigator.onLine) return resolve();

    console.log("[Axios] Waiting for network restoration...");
    const handler = () => {
      console.log("[Axios] Network restored → resuming requests");
      window.removeEventListener("online", handler);
      resolve();
    };
    window.addEventListener("online", handler);
  });
}

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function isLoginPath() {
  const path = window.location.pathname || "";
  return path === "/login" || path.startsWith("/login/");
}

function handleLogout(_force = true) {
  if (authLogoutInFlight) {
    return;
  }

  authLogoutInFlight = true;
  console.warn("[Axios] Logging out user due to authentication failure");

  setBlockLoginAutoRedirect();

  (async () => {
    try {
      await axios.post(
        "/api/logout-on-error",
        { confirmed: true },
        { withCredentials: true }
      );
    } catch (e) {
      console.warn("[Axios] logout-on-error failed:", e?.message || e);
    }

    if (isLoginPath()) {
      authLogoutInFlight = false;
      return;
    }

    window.location.assign("/login");
  })();
}
